/**
 * ✈️ AIRPORT SERVICE
 * Manages airport CRUD operations for admin
 */

import { buildApiUrl } from '@/utils/apiHelpers';

export interface AirportCoordinates {
  latitude: number;
  longitude: number;
}

export interface AirportStats {
  zoneCount: number;
  activeListingsCount: number;
}

export interface Airport {
  id: string;
  airportCode: string;
  airportName: string;
  city: string;
  state: string;
  country: string;
  coordinates: AirportCoordinates | null;
  timezone: string;
  isActive: boolean;
  stats?: AirportStats;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateAirportData {
  airportCode: string;
  airportName: string;
  city: string;
  state: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

export interface UpdateAirportData {
  airportCode?: string;
  airportName?: string;
  city?: string;
  state?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  isActive?: boolean;
}

export interface AirportResponse {
  success: boolean;
  data?: Airport;
  airports?: Airport[];
  error?: string;
  message?: string;
}

export const airportService = {
  /**
   * 📥 Get all airports
   */
  async getAirports(): Promise<AirportResponse> {
    try {
      const url = buildApiUrl('/admin/airports', 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Airports API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch airports (${response.status})`);
      }

      const result = await response.json();

      // Handle nested response structure: { success: true, data: { airports: [...], total: 5 } }
      const airports = result.airports || result.data?.airports || result.data || [];

      return {
        success: true,
        airports: Array.isArray(airports) ? airports : []
      };
    } catch (error) {
      console.error('❌ Error fetching airports:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch airports'
      };
    }
  },

  /**
   * 📥 Get single airport by ID
   */
  async getAirport(airportId: string): Promise<AirportResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}`, 'v2');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Airport API Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch airport (${response.status})`);
      }

      const result = await response.json();

      // Handle nested response structure: { success: true, data: { airport: {...} } }
      const airport = result.airport || result.data?.airport || result.data;

      return {
        success: true,
        data: airport
      };
    } catch (error) {
      console.error('❌ Error fetching airport:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch airport'
      };
    }
  },

  /**
   * ➕ Create new airport
   */
  async createAirport(data: CreateAirportData): Promise<AirportResponse> {
    try {
      const url = buildApiUrl('/admin/airports', 'v2');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Airport API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to create airport (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      // Handle nested response structure
      const airport = result.airport || result.data?.airport || result.data;

      return {
        success: true,
        data: airport,
        message: result.message || 'Airport created successfully'
      };
    } catch (error) {
      console.error('❌ Error creating airport:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create airport'
      };
    }
  },

  /**
   * ✏️ Update airport
   */
  async updateAirport(airportId: string, data: UpdateAirportData): Promise<AirportResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}`, 'v2');

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Airport API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to update airport (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      // Handle nested response structure
      const airport = result.airport || result.data?.airport || result.data;

      return {
        success: true,
        data: airport,
        message: result.message || 'Airport updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating airport:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update airport'
      };
    }
  },

  /**
   * 🗑️ Delete airport (soft delete)
   */
  async deleteAirport(airportId: string): Promise<AirportResponse> {
    try {
      const url = buildApiUrl(`/admin/airports/${airportId}`, 'v2');

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Delete Airport API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to delete airport (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Airport deleted successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting airport:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete airport'
      };
    }
  }
};
